import { FloatingFormItem } from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { FormatType } from '@/core/domain/reports/movements/dailyMovement/dailyMovement.types';
import { useGenerateInventoryRegisterBook } from '@/core/domain/reports/movements/purchase/ReportPurchase.hooks';
import { ReportInventoryRegisterBookParms } from '@/core/domain/reports/movements/purchase/ReportPurchase.types';
import { useReportGenerate, useWindowSize } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { Alert, Button, Col, Form, Input, Modal, ModalProps, Row } from 'antd';
import { useState } from 'react';

interface Props extends ModalProps {
  inventoryCurrentCountId: number;
  onCancel: () => void;
}

const InventoryReportP7Modal = ({
  inventoryCurrentCountId,
  ...props
}: Props) => {
  const { isMobile } = useWindowSize();
  const report = useReportGenerate();
  const generateInventoryRegisterBook = useGenerateInventoryRegisterBook();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const [subtitle, setSubtitle] = useState<string>();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const obj: ReportInventoryRegisterBookParms = {
        subtitle,
        inventoryCurrentCountId,
        format: format === 'CSV' ? FormatType.CSV : FormatType.PDF,
      };

      const result = await generateInventoryRegisterBook.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}
    setFileUnloading(format);
  };

  const handleView = async () => {
    await handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: `Relatório_P7_${inventoryCurrentCountId}.pdf`,
        fileType: 'application/pdf',
      }),
    );
    props.onCancel();
  };

  const handlePdf = async () => {
    await handleGenerateReport(FormatType.PDF, (result) =>
      report.downloadFile({
        result,
        fileName: `Relatório_P7_${inventoryCurrentCountId}.pdf`,
      }),
    );
    props.onCancel();
  };

  const handleCsv = async () => {
    await handleGenerateReport(FormatType.CSV, (result) =>
      report.downloadFile({
        result,
        fileName: `Relatório_P7_${inventoryCurrentCountId}.csv`,
      }),
    );
    props.onCancel();
  };

  const onCancel = () => {
    setSubtitle(undefined);
    props.onCancel();
  };

  return (
    <Modal
      {...props}
      title="Gerar relatório P7"
      width="70%"
      destroyOnClose
      footer={
        <SpaceForButton
          flexDirection={!isMobile ? 'row-reverse' : 'column'}
          justifyContent="flex-end"
        >
          <Button block onClick={onCancel}>
            Cancelar
          </Button>

          <Button
            block
            type="primary"
            onClick={handleView}
            key="viewReport"
            loading={fileLoading.REPORT_VIEW}
          >
            Visualizar
          </Button>

          <Button
            block
            type="primary"
            onClick={handlePdf}
            key="CSV"
            loading={fileLoading.PDF}
          >
            Gerar PDF
          </Button>

          <Button
            block
            type="primary"
            onClick={handleCsv}
            key="CSV"
            loading={fileLoading.CSV}
          >
            Gerar CSV
          </Button>
        </SpaceForButton>
      }
    >
      <Form>
        <Row gutter={[16, 16]}>
          <Alert
            type="info"
            description=" O relatório P7 é um documento contábil que registra o inventário de uma
      empresa, listando os produtos, matérias-primas e materiais de embalagem.
      Ele é também conhecido como Livro de Registro de Inventário."
          />

          <Col xs={24} sm={24} md={12}>
            <FloatingFormItem label="Legenda" name="subtitle">
              <Input onChange={({ target: { value } }) => setSubtitle(value)} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default InventoryReportP7Modal;
